import { useCallback, useEffect, useState } from 'react'
import { api, type PunchState } from '../api'
import type { Category, Client, Punch } from '../../shared/types'
import { formatHours, parseDuration } from '../../shared/duration'
import { appTimezone, dateInTz, timeInTz } from '../datetime'
import { pushError, pushToast } from '../toast'
import CategoryCombo from './CategoryCombo'
import ClientCombo from './ClientCombo'

function hhmmToMin(v: string): number {
  const [h, m] = v.split(':').map(Number)
  return h * 60 + (m || 0)
}
function minToHHMM(min: number): string {
  const m = ((Math.round(min) % 1440) + 1440) % 1440
  return `${String(Math.floor(m / 60)).padStart(2, '0')}:${String(m % 60).padStart(2, '0')}`
}
function fmtElapsed(ms: number): string {
  const s = Math.max(0, Math.floor(ms / 1000))
  const h = Math.floor(s / 3600)
  const m = Math.floor((s % 3600) / 60)
  const ss = s % 60
  return (h ? `${h}:${String(m).padStart(2, '0')}` : `${m}`) + `:${String(ss).padStart(2, '0')}`
}
function segMinutes(seg: Punch): number {
  if (!seg.ended_at) return 0
  return Math.round((Date.parse(seg.ended_at) - Date.parse(seg.started_at)) / 60000)
}

// Cloud-synced punch clock: state lives in D1, so a running timer can be
// started on one device and stopped on another. Polls every 20s and on focus.
export default function PunchClock({ clients, allCats, onLogged }: { clients: Client[]; allCats: Category[]; onLogged: () => void }) {
  const [st, setSt] = useState<PunchState>({ running: null, segs: [] })
  const [now, setNow] = useState(() => Date.now())
  const [finishing, setFinishing] = useState<Punch | null>(null)
  const [busy, setBusy] = useState(false)
  const clientById = new Map(clients.map((c) => [c.id, c]))

  const refresh = useCallback(async () => {
    try {
      setSt(await api.getPunches())
    } catch {
      /* transient; keep prior state */
    }
  }, [])

  useEffect(() => {
    refresh()
    const poll = setInterval(refresh, 20000)
    const onVis = () => {
      if (!document.hidden) refresh()
    }
    document.addEventListener('visibilitychange', onVis)
    window.addEventListener('focus', refresh)
    return () => {
      clearInterval(poll)
      document.removeEventListener('visibilitychange', onVis)
      window.removeEventListener('focus', refresh)
    }
  }, [refresh])

  useEffect(() => {
    if (!st.running) return
    const t = setInterval(() => setNow(Date.now()), 1000)
    return () => clearInterval(t)
  }, [st.running])

  async function punch(clientId: number) {
    if (busy || st.running?.client_id === clientId) return
    setBusy(true)
    try {
      setSt(await api.startPunch(clientId))
      setNow(Date.now())
    } catch (e) {
      pushError(`Couldn't start: ${(e as Error).message}`)
    } finally {
      setBusy(false)
    }
  }

  async function stop() {
    if (busy || !st.running) return
    setBusy(true)
    try {
      const res = await api.stopPunch()
      setSt(res)
      const newest = res.segs.reduce<Punch | null>((a, b) => (a && a.id > b.id ? a : b), null)
      if (newest) setFinishing(newest)
    } catch (e) {
      pushError(`Couldn't stop: ${(e as Error).message}`)
    } finally {
      setBusy(false)
    }
  }

  async function discard(id: number) {
    setSt((s) => ({ ...s, segs: s.segs.filter((x) => x.id !== id) }))
    try {
      await api.deletePunch(id)
    } catch (e) {
      pushError(`Couldn't discard: ${(e as Error).message}`)
      refresh()
    }
  }

  const running = st.running
  const runClient = running ? clientById.get(running.client_id) : undefined
  const runAccent = runClient?.accent_color ?? '#3b6ea5'

  return (
    <div className="rounded-lg border border-slate-200 bg-white">
      <div className="flex flex-wrap items-center justify-between gap-2 border-b border-slate-100 px-4 py-2">
        <h2 className="text-xs font-semibold uppercase tracking-wide text-slate-500">Time punch</h2>
        {running && (
          <div className="flex items-center gap-3">
            <span className="flex items-center gap-2 text-sm">
              <span className="h-2.5 w-2.5 animate-pulse rounded-full" style={{ backgroundColor: runAccent }} />
              <span className="font-medium text-slate-800">{runClient?.simple_name ?? 'Running'}</span>
              <span className="tabular text-slate-500">{fmtElapsed(now - Date.parse(running.started_at))}</span>
            </span>
            <button className="rounded-md bg-red-600 px-3 py-1 text-sm font-medium text-white hover:bg-red-700 disabled:opacity-50" disabled={busy} onClick={stop}>
              ■ Stop
            </button>
          </div>
        )}
      </div>

      <div className="p-3">
        {clients.length === 0 ? (
          <p className="py-2 text-center text-sm text-slate-400">Add a client to start punching time.</p>
        ) : (
          <div className="grid grid-cols-2 gap-2 sm:grid-cols-3 md:grid-cols-4">
            {clients.map((c) => {
              const accent = c.accent_color ?? '#3b6ea5'
              const active = running?.client_id === c.id
              return (
                <button
                  key={c.id}
                  onClick={() => punch(c.id)}
                  disabled={busy}
                  title={active ? 'Running' : running ? 'Switch to this client' : 'Start timing'}
                  className={'flex items-center gap-2 rounded-md border px-2.5 py-2 text-left text-sm disabled:opacity-60 ' + (active ? 'text-white' : 'bg-white text-slate-800 hover:bg-slate-50')}
                  style={active ? { backgroundColor: accent, borderColor: accent } : { borderColor: accent, borderLeftWidth: 4 }}
                >
                  <span className={'h-2 w-2 shrink-0 rounded-full ' + (active ? 'animate-pulse bg-white' : '')} style={active ? undefined : { backgroundColor: accent }} />
                  <span className="min-w-0 truncate font-medium">{c.simple_name}</span>
                  {active && running && <span className="tabular ml-auto text-xs">{fmtElapsed(now - Date.parse(running.started_at))}</span>}
                </button>
              )
            })}
          </div>
        )}

        {st.segs.length > 0 && (
          <div className="mt-3 border-t border-slate-100 pt-3">
            <div className="mb-1 text-xs font-semibold uppercase tracking-wide text-slate-400">Open logs to finish</div>
            <ul className="flex flex-col divide-y divide-slate-100">
              {st.segs.map((seg) => {
                const cl = clientById.get(seg.client_id)
                const accent = cl?.accent_color ?? '#94a3b8'
                return (
                  <li key={seg.id} className="flex items-center gap-2 py-1.5 text-sm">
                    <span className="h-2.5 w-2.5 shrink-0 rounded-full" style={{ backgroundColor: accent }} />
                    <span className="min-w-0 flex-1 truncate text-slate-700">{cl?.simple_name ?? 'Client'}</span>
                    <span className="tabular text-slate-500">{formatHours(segMinutes(seg))} h</span>
                    <button className="rounded bg-slate-900 px-2 py-0.5 text-xs font-medium text-white hover:bg-slate-700" onClick={() => setFinishing(seg)}>
                      Finish
                    </button>
                    <button className="text-slate-300 hover:text-red-500" title="Discard" onClick={() => discard(seg.id)}>
                      ✕
                    </button>
                  </li>
                )
              })}
            </ul>
          </div>
        )}
      </div>

      {finishing && (
        <FinishModal
          seg={finishing}
          clients={clients}
          allCats={allCats}
          onCancel={() => setFinishing(null)}
          onSaved={async () => {
            const id = finishing.id
            setFinishing(null)
            try {
              await api.deletePunch(id)
            } catch {
              /* ignore */
            }
            await refresh()
            onLogged()
          }}
        />
      )}
    </div>
  )
}

function FinishModal({
  seg,
  clients,
  allCats,
  onCancel,
  onSaved,
}: {
  seg: Punch
  clients: Client[]
  allCats: Category[]
  onCancel: () => void
  onSaved: () => void
}) {
  const tz = appTimezone()
  const startD = new Date(seg.started_at)
  const [clientId, setClientId] = useState<number | null>(seg.client_id)
  const [description, setDescription] = useState('')
  const [categoryId, setCategoryId] = useState<number | null>(null)
  const [date, setDate] = useState(dateInTz(tz, startD))
  const [startTime, setStartTime] = useState(timeInTz(tz, startD))
  const [durText, setDurText] = useState(formatHours(segMinutes(seg)))
  const [saving, setSaving] = useState(false)
  const cats = clientId != null ? allCats.filter((c) => c.client_id === clientId) : []
  const durMinutes = parseDuration(durText) ?? 0

  async function save() {
    if (clientId == null) return
    setSaving(true)
    try {
      const body: Record<string, unknown> = { client_id: clientId, entry_date: date, description, category_id: categoryId, duration_minutes: durMinutes }
      if (startTime) {
        body.start_time = startTime
        body.end_time = minToHHMM(hhmmToMin(startTime) + durMinutes)
      }
      await api.createEntry(body as any)
      pushToast('Time logged')
      onSaved()
    } catch (e) {
      pushError(`Couldn't log time: ${(e as Error).message}`)
      setSaving(false)
    }
  }

  return (
    <div className="fixed inset-0 z-40 flex items-start justify-center overflow-auto bg-black/40 p-4 sm:p-6">
      <div className="mt-12 w-full max-w-md rounded-lg bg-white shadow-xl">
        <div className="flex items-center justify-between border-b border-slate-200 px-5 py-3">
          <h2 className="text-lg font-semibold text-slate-900">Finish time log</h2>
          <button className="text-slate-400 hover:text-slate-700" onClick={onCancel}>
            ✕
          </button>
        </div>
        <div className="flex flex-col gap-3 px-5 py-4 text-sm">
          <label className="block">
            <span className="mb-1 block text-xs font-medium text-slate-600">Client</span>
            <div className="rounded-md border border-slate-300 px-1">
              <ClientCombo value={clientId} clients={clients} onCommit={(id) => { setClientId(id); setCategoryId(null) }} gridKeyDown={() => {}} registerRef={() => {}} />
            </div>
          </label>
          <label className="block">
            <span className="mb-1 block text-xs font-medium text-slate-600">Description</span>
            <input className={fieldCls} value={description} onChange={(e) => setDescription(e.target.value)} autoFocus />
          </label>
          <label className="block">
            <span className="mb-1 block text-xs font-medium text-slate-600">Category</span>
            <div className={'rounded-md border border-slate-300 px-1 ' + (clientId == null ? 'bg-slate-50' : '')}>
              <CategoryCombo value={categoryId} categories={cats} disabled={clientId == null} onCommit={setCategoryId} gridKeyDown={() => {}} registerRef={() => {}} />
            </div>
          </label>
          <div className="grid grid-cols-3 gap-3">
            <label className="block">
              <span className="mb-1 block text-xs font-medium text-slate-600">Date</span>
              <input type="date" className={fieldCls + ' tabular'} value={date} onChange={(e) => setDate(e.target.value)} />
            </label>
            <label className="block">
              <span className="mb-1 block text-xs font-medium text-slate-600">Start</span>
              <input type="time" className={fieldCls + ' tabular'} value={startTime} onChange={(e) => setStartTime(e.target.value)} />
            </label>
            <label className="block">
              <span className="mb-1 block text-xs font-medium text-slate-600">Duration (h)</span>
              <input className={fieldCls + ' tabular'} value={durText} onChange={(e) => setDurText(e.target.value)} />
            </label>
          </div>
        </div>
        <div className="flex items-center justify-between border-t border-slate-200 px-5 py-3">
          <button className="text-sm text-slate-500 hover:text-slate-800" onClick={onCancel}>
            Cancel
          </button>
          <button className="rounded-md bg-slate-900 px-4 py-1.5 text-sm font-medium text-white hover:bg-slate-700 disabled:opacity-50" disabled={clientId == null || saving} onClick={save}>
            {saving ? 'Logging…' : 'Log time'}
          </button>
        </div>
      </div>
    </div>
  )
}

const fieldCls = 'w-full rounded-md border border-slate-300 bg-white px-2 py-1.5 text-sm text-slate-900 focus:border-[var(--accent)]'
